'use client';

import {
  CircleAlert, CircleCheck, CircleDot, Zap, CheckCircle2, XCircle,
  AlertTriangle, HelpCircle, Hourglass, RefreshCw,
  FileText, FolderOpen, Folder, ClipboardList, Files, FileEdit, Package, Paperclip,
  MessageSquare, Smartphone, Mail, Radio, Megaphone, Bell,
  BarChart3, TrendingUp, TrendingDown, DollarSign, Banknote, Receipt, Calculator,
  Wrench, Settings, Hammer, Link, Lock, Key, Search, Monitor, Laptop,
  Brain, Bot, Sparkles, Lightbulb, Target, Star,
  Palette, Image as ImageIcon, Ruler, Building2, Landmark, Home, Building, School2,
  Globe, Flame, Gem, Clapperboard, Camera, Music,
  ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ArrowUpRight,
  Calendar, CalendarDays, Clock, AlarmClock,
  Rocket, Pin, MapPin, PartyPopper, Dumbbell, Volume2, Shield, Ban,
  ChevronDown, ChevronUp, Square, Circle, Diamond, Eye,
  SquareStack, Cpu, HardDrive, Crown, Apple, HardHat, UserCog,
  Presentation, Download, FileSpreadsheet, FileType, FileCode,
  Tag, Timer, ThumbsUp, ThumbsDown, Send, Plus, Minus, X, Check,
  Play, Pause, SkipForward, Activity, Network,
  Layers, Box, Cuboid, Video, Upload, PenTool, Paintbrush, Wand2,
  type LucideIcon,
} from 'lucide-react';
import React from 'react';

/** Emoji string → Lucide icon mapping */
const ICON_MAP: Record<string, LucideIcon> = {
  '🔴': CircleAlert,
  '🟢': CircleCheck,
  '🟡': CircleDot,
  '⚡': Zap,
  '✅': CheckCircle2,
  '❌': XCircle,
  '⚠️': AlertTriangle,
  '❓': HelpCircle,
  '⏳': Hourglass,
  '🔄': RefreshCw,
  '📄': FileText,
  '📂': FolderOpen,
  '📁': Folder,
  '📋': ClipboardList,
  '📑': Files,
  '📝': FileEdit,
  '📦': Package,
  '📎': Paperclip,
  '💬': MessageSquare,
  '📱': Smartphone,
  '📧': Mail,
  '📡': Radio,
  '📢': Megaphone,
  '🔔': Bell,
  '📊': BarChart3,
  '📈': TrendingUp,
  '📉': TrendingDown,
  '💰': DollarSign,
  '💵': Banknote,
  '🧾': Receipt,
  '🧮': Calculator,
  '🔧': Wrench,
  '⚙️': Settings,
  '🛠️': Hammer,
  '🔗': Link,
  '🔒': Lock,
  '🔑': Key,
  '🔍': Search,
  '🖥️': Monitor,
  '💻': Laptop,
  '🧠': Brain,
  '🤖': Bot,
  '✨': Sparkles,
  '💡': Lightbulb,
  '🎯': Target,
  '⭐': Star,
  '🌟': Star,
  '🎨': Palette,
  '🖼️': ImageIcon,
  '📐': Ruler,
  '🏗️': Building2,
  '🏛️': Landmark,
  '🏠': Home,
  '🏢': Building,
  '🏫': School2,
  '🌐': Globe,
  '🔥': Flame,
  '💎': Gem,
  '🎬': Clapperboard,
  '📷': Camera,
  '🎵': Music,
  '🔙': ArrowLeft,
  '➡️': ArrowRight,
  '→': ArrowRight,
  '←': ArrowLeft,
  '⬆️': ArrowUp,
  '⬇️': ArrowDown,
  '↗️': ArrowUpRight,
  '👷': HardHat,
  '👨‍💻': UserCog,
  '👩‍💻': UserCog,
  '👑': Crown,
  '🐻': Activity,
  '🍏': Apple,
  '🪟': SquareStack,
  '🦿': Cpu,
  '💾': HardDrive,
  '📅': Calendar,
  '📆': CalendarDays,
  '🕐': Clock,
  '⏰': AlarmClock,
  '🚀': Rocket,
  '📌': Pin,
  '📍': MapPin,
  '🎉': PartyPopper,
  '💪': Dumbbell,
  '🔊': Volume2,
  '📻': Radio,
  '🛡️': Shield,
  '⛔': Ban,
  '▼': ChevronDown,
  '▲': ChevronUp,
  '■': Square,
  '●': Circle,
  '◆': Diamond,
  '👁️': Eye,
  '📙': Presentation,
  '📃': FileType,
  '🧊': Cuboid,
  '⏱️': Timer,
  '👍': ThumbsUp,
  '👎': ThumbsDown,
  '🏷️': Tag,
  '💠': Diamond,
  '🎛️': Settings,
  '🌿': PenTool,
  '🪵': Paintbrush,
  '🔩': Wrench,
};

interface EmojiIconProps {
  emoji: string;
  className?: string;
  size?: number;
  /** fallback to original emoji text */
  fallback?: boolean;
}

/** Renders a Lucide icon corresponding to the emoji. Falls back to the emoji string if not mapped. */
export default function EmojiIcon({ emoji, className = 'w-4 h-4 inline-block', size, fallback = true }: EmojiIconProps) {
  const Icon = ICON_MAP[emoji];
  if (Icon) return <Icon className={className} size={size} />;
  if (fallback) return <span>{emoji}</span>;
  return null;
}

/** Get the Lucide icon component for an emoji, or undefined */
export function getIconForEmoji(emoji: string): LucideIcon | undefined {
  return ICON_MAP[emoji];
}

/** File extension to Lucide icon mapping */
export function getFileIcon(ext?: string, type?: string): LucideIcon {
  if (type === 'dir') return Folder;
  const e = (ext || '').toLowerCase().replace('.', '');
  const map: Record<string, LucideIcon> = {
    pdf: Files, doc: FileEdit, docx: FileEdit, hwp: FileType, hwpx: FileType,
    xls: FileSpreadsheet, xlsx: FileSpreadsheet, csv: FileSpreadsheet,
    ppt: Presentation, pptx: Presentation,
    dwg: Ruler, dxf: Ruler,
    jpg: ImageIcon, jpeg: ImageIcon, png: ImageIcon, gif: ImageIcon,
    bmp: ImageIcon, tiff: ImageIcon, psd: Palette, svg: Palette,
    skp: Cuboid, rvt: Building2, ifc: Building2, obj: Cuboid, stl: Cuboid,
    fbx: Cuboid, '3ds': Cuboid,
    zip: Package, rar: Package, '7z': Package, tar: Package, gz: Package,
    mp4: Video, mov: Video, avi: Video, mkv: Video,
    mp3: Music, wav: Music, flac: Music,
    txt: FileText, md: FileText, json: FileCode, xml: FileCode,
    yaml: FileCode, yml: FileCode,
    js: FileCode, ts: FileCode, py: FileCode, html: Globe, css: Globe,
  };
  return map[e] || FileText;
}

// Re-export the map for direct access
export { ICON_MAP };
